import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Award, Users, Target, Eye, Building } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import hospital from "../assets/hospitalimg.png";
import drashoka from "../assets/drashoka.JPG";
import drvarsha from "../assets/drvarsha.JPG";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Compassionate Care',
      description: 'We treat every patient with empathy, dignity, and respect',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to providing the highest quality medical services',
    },
    {
      icon: Users,
      title: 'Patient-Centered',
      description: 'Your health and well-being are at the center of everything we do',
    },
    {
      icon: Target,
      title: 'Affordability',
      description: 'Quality healthcare accessible to all economic backgrounds',
    },
  ];

  const milestones = [
    { year: '2014', title: 'Foundation', description: 'Construction of Ashoka Hospital began' },
    { year: '2015', title: 'Opening', description: 'Hospital opened with basic services' },
    { year: '2020', title: 'Expansion', description: 'Added ICU and advanced diagnostic facilities' },
    { year: '2025', title: 'Today', description: 'Innaguration of the State Of The Art Cath Lab' },
  ];

  const founders = [
    {
      name: 'Dr. Ashok Bangarshettar',
      title: 'Founder & Orthopaedic Surgeon',
      description: 'Highly skilled orthopaedic surgeon with extensive experience in trauma care and Joint Replacement surgical procedures.',
      image: drashoka,
    },
    {
      name: 'Dr. Varsha Bangarshettar',
      title: 'Co-Founder & Ophthalmologist',
      description: 'Dedicated to providing excellent eye care facilities to the public with compassion and expertise.',
      image: drvarsha,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src={hospital}
          alt="About Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-blue-900/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-white mb-4"
          >
            About <span className="text-sky-300">Ashoka Hospital</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl mx-auto"
          >
            Committed to providing affordable, quality healthcare since 2015
          </motion.p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="border-none shadow-2xl bg-gradient-to-br from-sky-50 to-blue-50 overflow-hidden">
                <CardContent className="p-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-sky-500 rounded-2xl flex items-center justify-center mb-6">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-4 text-gray-800">
                    Our Mission
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed max-w-md">
                    To provide good health care facilities for middle and lower-class people at affordable prices, ensuring that quality medical care is accessible to everyone in our community.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="border-none shadow-2xl bg-gradient-to-br from-blue-50 to-cyan-50 overflow-hidden">
                <CardContent className="p-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mb-6">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-4 text-gray-800">
                    Our Vision
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed max-w-md">
                    To be the leading healthcare provider in the region, known for our commitment to patient care, medical excellence, and making quality healthcare accessible to all.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Our Story */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Our <span className="text-sky-500">Story</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A journey of compassion, dedication, and commitment to affordable healthcare
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-none shadow-xl bg-white">
              <CardContent className="p-10">
                <div className="flex items-start gap-6 mb-8">
                  {/* <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building className="w-8 h-8 text-white" />
                  </div> */}
                  <div>
                    {/* <h3 className="text-2xl font-bold mb-4 text-gray-800"></h3> */}
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Ashoka Super specialty hospital (Swasthya heart and kidney care) where the heart of healing hearts meets the joy of health experiences. Swasthya heart and kidney care is the hearts haven, in the vibrant heart of Hubballi city. Our specialized tertiary cardiac care centre is more than just a hospital. It's a state-of-the-art heart resort designated to make your cardiac vascular journey a delightful experience.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      We have equipped with cutting edge technology that rivals a spaceship including our marvelous cath lab unit, cardiac surgery unit, and magical cardiac intensive care unit.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      Our hospital is established in the year 2015 under Dr Ashok V Bangarshettar and Dr Varsha Bangarshettar our visionary leader and dynamic orthopaedic surgeon. He is like a wizard with magical touch bringing more than a decade of experience in healing the patient with soft touch.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                  {milestones.map((milestone, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="text-center"
                    >
                      <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-xl font-bold text-sky-600">{milestone.year}</span>
                      </div>
                      <h4 className="font-bold text-gray-800 mb-2">{milestone.title}</h4>
                      <p className="text-sm text-gray-600">{milestone.description}</p>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Our <span className="text-sky-500">Founders</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the visionary leaders behind Ashoka Hospital
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all overflow-hidden group">
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <h3 className="text-2xl font-bold mb-1">{founder.name}</h3>
                      <p className="text-sky-300 font-medium">{founder.title}</p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600 leading-relaxed">{founder.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Our <span className="text-sky-500">Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our commitment to excellence in healthcare
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 bg-white h-full">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-sky-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg"
                    >
                      <value.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;